import './ShareSheet.sass';

import React, { FC, useCallback, useMemo } from 'react';
import copyToClipboard from 'copy-text-to-clipboard';
import { useDispatch } from 'react-redux';

import { ShareSheetProps } from './ShareSheet.interface';
import { HorizontalScroll, List, ListItem } from '$uikit';
import { toastActions } from '$store/toast';
import { postsActions } from '$store/posts';
import { showModal } from '$navigation/helper';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { mainActions } from '$store/main';
import { IcCopy28, IcMessage28, IcShare24, IcWrite28 } from '$assets';
import { useTranslator } from '$hooks';
import { SocialButtons } from '$shared/components';

export const ShareSheet: FC<ShareSheetProps> = (props) => {
  const { link, title, disableInternalShare = false } = props;
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleWall = useCallback(() => {
    dispatch(mainActions.setPopup(null));
    dispatch(postsActions.share(link!));
  }, [dispatch, link]);

  const handleMessage = useCallback(() => {
    dispatch(mainActions.setPopup(null));
    showModal('write', false, {
      share: `${link!.type}${link!.object.id}`,
    });
  }, [dispatch, link]);

  const url = useMemo(() => {
    switch (link.type) {
      case 'personal_subscription':
        return `https://${location.host}/subscription/author/${link.object.id}`;
      case 'battle':
        return `https://${location.host}/battle/${link.object.id}`;
      case 'app':
        return `https://${location.host}/app/${link.object.id}`;
      case 'reel':
        return `https://${location.host}/clips?reel_id=${link.object}`;
      case 'live':
        return `https://${location.host}/${link.object}/live`;
      default:
        return `https://${location.host}${location.pathname}?w=${link!.type}${
          link!.object.id
        }`;
    }
  }, [link]);

  const urlEncoded = useMemo(() => {
    return encodeURIComponent(url);
  }, [url]);

  const handleOther = useCallback(() => {
    /* @ts-ignore */
    if (navigator.canShare) {
      navigator.share({
        url,
      });
    } else {
      copyToClipboard(url);
      dispatch(toastActions.setToastSuccess(t('link_copied')));
    }
  }, [dispatch, url, t]);

  const handleCopy = useCallback(() => {
    copyToClipboard(url);
    dispatch(toastActions.setToastSuccess(t('link_copied')));
  }, [dispatch, url, t]);

  function renderOtherButton() {
    /* @ts-ignore */
    if (navigator.canShare) {
      return (
        <ListItem icon={<IcShare24 />} onClick={handleOther}>
          {t('share_other')}
        </ListItem>
      );
    } else {
      return null;
    }
  }

  return (
    <BottomSheet title={title}>
      <div className="ShareSheet__buttons">
        <List>
          {!disableInternalShare && (
            <>
              <ListItem icon={<IcWrite28 />} onClick={handleWall}>
                {t('share_wall')}
              </ListItem>
              <ListItem icon={<IcMessage28 />} onClick={handleMessage}>
                {t('share_message')}
              </ListItem>
            </>
          )}
          {renderOtherButton()}
          <ListItem icon={<IcCopy28 />} onClick={handleCopy}>
            {t('share_link')}
          </ListItem>
        </List>
      </div>
      <HorizontalScroll>
        <SocialButtons link={urlEncoded} className="ShareSheet__socials" />
      </HorizontalScroll>
    </BottomSheet>
  );
};
